import Link from 'next/link';
import Card from 'ui/components/atoms/Card';
import convertStringToSlug from 'ui/helpers/utils/convertStringToSlug';
import dateFormatter from 'ui/helpers/utils/dateFormatter';
import eventClickPromo from 'ui/lib/gtm/eventClickPromo';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import useIsNativeView from '../../../hooks/useIsNativeView';
import { Article } from '../../../interface';
import generateImageResizer from '../../../helpers/utils/generateImageResizer';
import eventClickPromoHomePage from '../../../lib/gtm/eventClickPromoHomePage';
import eventClickHomepage from '../../../helpers/emarsys/homepage/eventClickHomepage';

declare const window: WindowTypes;

interface IParams {
  data: Article;
  optimize?: boolean;
  isHomepage?: boolean;
}

interface IProps {
  data: Article;
  optimize?: boolean;
  idx?: number;
  categoryGTM?: string;
  handleCollectDataLayer?: (props: {
    id: string | number;
    name: string;
    creative: string;
    position: string;
  }) => void;
  enableSharing?: boolean;
  isHomepage?: boolean;
  handleShowModalExpiredPromo?: () => void;
}

const CardOtherPromo = ({ data, optimize, isHomepage = false }: IParams) => {
  return (
    <div className="">
      <img
        src={generateImageResizer(data?.heroImageLink, 360, 'webp') || ''}
        alt={data?.altImage || ''}
        width={360}
        height={180}
        loading={optimize ? 'eager' : 'lazy'}
        fetchPriority={optimize ? 'high' : 'low'}
        decoding="async"
        className="w-full aspect-video object-cover mb-4"
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      />
      <div className="h-10 text-ellipsis overflow-hidden lg:h-14">
        <h3
          className={`${isHomepage ? 'text-base text-black' : 'text-sm text-reliableBlack'} text-start font-semibold line-clamp-2 lg:text-lg lg:font-semibold`}
        >
          {data?.titlePage}
        </h3>
      </div>
      <p className="text-[12px] text-start font-normal text-reliableBlack70 lg:text-sm">
        <span>{dateFormatter(data?.startDate)}</span>
        <span> - </span>
        <span>{dateFormatter(data?.endDate)}</span>
      </p>
    </div>
  );
};

const OtherPromoCard = ({
  categoryGTM,
  handleCollectDataLayer,
  data,
  enableSharing = false,
  handleShowModalExpiredPromo,
  optimize,
  isHomepage = false,
  idx,
}: IProps) => {
  const isNativeView = useIsNativeView();
  const mobileAppQuery = isNativeView ? '?mobileApp=true' : '';

  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

  const handleGoToPromoDetail = (e, contentCategory, slug) => {
    if (isNativeView) {
      e.preventDefault();
      if (typeof window !== 'undefined') {
        window?.androidApp?.goToPromoDetail(contentCategory, slug);
        window?.webkit?.messageHandlers?.iosApp?.postMessage(
          `goToPromoDetail/${contentCategory}/${slug}`
        );
      }
    }
  };

  const ref = useIsElementVisible(
    () =>
      handleCollectDataLayer({
        id: data?.id,
        name: data?.titlePage?.toLowerCase(),
        creative: `/promosi/${data?.contentCategory?.name
          ?.toLowerCase()
          ?.replace(/ /g, '-')}/${data?.slug}`,
        position: `${idx + 1} - promosi ${data?.contentCategory?.name
          ?.toLowerCase()
          ?.replace(/-/g, ' ')}`,
      }),
    [data]
  );

  return (
    <Card
      ref={ref}
      key={`${data?.slug}-${idx}`}
      className={`h-full ${isHomepage ? '' : 'border-b'} border-[#6B6B6B] snap-center lg:border-reliableBlack70`}
    >
      {enableSharing ? (
        <CardOtherPromo data={data} />
      ) : (
        <>
          {data.status === 'EXPIRED' ? (
            // When Promo EXPIRED
            <div className="cursor-pointer" onClick={handleShowModalExpiredPromo}>
              <CardOtherPromo data={data} optimize={optimize} />
            </div>
          ) : (
            // When Status Promo PUBLISH
            <Link
              id={`card-dtl-promo-${data.slug}`}
              href={`${PUBLIC_URL}/promosi/${convertStringToSlug(
                data?.contentCategory?.name
              )}/${data.slug}${mobileAppQuery}`}
              onClick={(e) => {
                if (isHomepage) {
                  eventClickPromoHomePage(
                    {
                      id: data?.id,
                      name: data?.titlePage?.toLowerCase(),
                      creative: `/promosi/${data?.contentCategory?.name
                        ?.toLowerCase()
                        ?.replace(/ /g, '-')}/${data?.slug}`,
                      position: `${idx + 1} - promosi ${data?.contentCategory?.name
                        ?.toLowerCase()
                        ?.replace(/-/g, ' ')}`,
                    },
                    'promo'
                  );
                  eventClickHomepage('homepage auto2000', 'promo', 'pilih promo', {
                    promoName: data?.titlePage,
                  });
                } else {
                  eventClickPromo({
                    title: data?.titlePage,
                    slug: data?.slug,
                    category: data?.contentCategory?.name,
                    id: data?.id,
                    index: idx + 1,
                    anotherCategory: categoryGTM,
                  });
                  handleGoToPromoDetail(e, data?.contentCategory?.name, data?.slug);
                }
              }}
            >
              <CardOtherPromo data={data} optimize={optimize} />
            </Link>
          )}
        </>
      )}
    </Card>
  );
};

export default OtherPromoCard;
